import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

function sanitizeFilename(name) {
  const base = String(name || 'document.pdf').trim() || 'document.pdf';
  return base.replace(/[/\\?%*:|"<>]/g, '_');
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      resolve(dataUrl.split(',')[1] || '');
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

function isShareCancelled(err) {
  if (!err) return false;
  if (err.name === 'AbortError') return true;
  const message = String(err.message || err.errorMessage || '').toLowerCase();
  if (/cancel/.test(message)) return true;
  const code = String(err.code || '');
  return code === 'OS-PLUG-GEN-001' || /cancel/i.test(code);
}

function openNativeShareSheet(uri, filename) {
  const shareOptions = {
    title: filename,
    files: [uri],
    url: uri,
    dialogTitle: 'Save or share PDF',
  };
  void Share.share(shareOptions).catch((err) => {
    if (!isShareCancelled(err)) {
      console.warn('[downloadBlob] Share failed', err);
    }
  });
}

async function downloadViaCapacitorShare(blob, filename) {
  const safeName = sanitizeFilename(filename);
  const base64 = await blobToBase64(blob);
  await Filesystem.writeFile({
    path: safeName,
    data: base64,
    directory: Directory.Cache,
  });
  const { uri } = await Filesystem.getUri({
    directory: Directory.Cache,
    path: safeName,
  });
  openNativeShareSheet(uri, safeName);
}

async function downloadViaWebShare(blob, filename, mimeType) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }
  try {
    const safeName = sanitizeFilename(filename);
    const file = new File([blob], safeName, { type: mimeType });
    const payload = { files: [file], title: safeName };
    if (typeof navigator.canShare === 'function' && !navigator.canShare(payload)) {
      return false;
    }
    void navigator.share(payload).catch((err) => {
      if (!isShareCancelled(err)) {
        console.warn('[downloadBlob] Web share failed', err);
      }
    });
    return true;
  } catch (err) {
    return false;
  }
}

function downloadViaAnchor(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = sanitizeFilename(filename);
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function openPdfInNewTab(blob) {
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  if (opened) {
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return true;
  }
  window.URL.revokeObjectURL(url);
  return false;
}

function isMobileWebBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/**
 * Save or share a blob as a file. Works in desktop browsers, mobile Safari, and Capacitor iOS/Android.
 */
export async function downloadBlob(blob, filename, mimeType = 'application/pdf') {
  if (!blob) {
    throw new Error('Nothing to download');
  }
  const file = blob instanceof Blob ? blob : new Blob([blob], { type: mimeType });

  if (Capacitor.isNativePlatform()) {
    try {
      await downloadViaCapacitorShare(file, filename);
      return;
    } catch (err) {
      const code = err?.code || err?.errorMessage || '';
      if (String(code).includes('UNIMPLEMENTED') || /not implemented/i.test(String(err?.message || ''))) {
        // Native plugins missing (stale build) — fall through to web share / anchor.
      } else {
        throw err;
      }
    }
  }

  // Desktop web: always trigger a direct browser download.
  if (!isMobileWebBrowser()) {
    downloadViaAnchor(file, filename);
    return;
  }

  if (await downloadViaWebShare(file, filename, mimeType)) {
    return;
  }

  const isMobileSafari =
    typeof navigator !== 'undefined'
    && /iPad|iPhone|iPod/i.test(navigator.userAgent)
    && mimeType === 'application/pdf';
  if (isMobileSafari && openPdfInNewTab(file)) {
    return;
  }

  downloadViaAnchor(file, filename);
}

export function parseContentDispositionFilename(disposition, fallback) {
  return disposition?.match(/filename="?([^";]+)"?/)?.[1] || fallback;
}

/**
 * Ensure an axios/blob response is a real PDF (not an HTML/JSON error body with 200/4xx).
 * Returns a normalized application/pdf Blob.
 */
export async function ensurePdfBlob(blob, fallbackMessage = 'Failed to load PDF') {
  if (!blob) {
    throw new Error(fallbackMessage);
  }
  const raw = blob instanceof Blob ? blob : new Blob([blob]);
  const headerBuf = await raw.slice(0, 5).arrayBuffer();
  const headerBytes = new Uint8Array(headerBuf);
  const header = String.fromCharCode(
    headerBytes[0] || 0,
    headerBytes[1] || 0,
    headerBytes[2] || 0,
    headerBytes[3] || 0,
    headerBytes[4] || 0,
  );
  if (header === '%PDF-') {
    return raw.type === 'application/pdf'
      ? raw
      : new Blob([raw], { type: 'application/pdf' });
  }

  let detail = fallbackMessage;
  try {
    const text = (await raw.text()).trim();
    if (text) {
      try {
        const parsed = JSON.parse(text);
        detail = parsed.error || parsed.detail || parsed.message || detail;
      } catch {
        if (text.length < 280 && !text.startsWith('<')) {
          detail = text;
        }
      }
    }
  } catch {
    // keep fallback
  }
  throw new Error(detail);
}

/** Native Capacitor apps save PDFs via the system share sheet, not a direct download. */
export function usesShareSheetForDownload() {
  return Capacitor.isNativePlatform();
}
